// DocBadges — frontmatter type/status pills (RFC-0010 §5, PD-0016 render half).
//
// Renders `docType` and `docStatus` (persisted by RFC-0003, serialized on the
// doc-detail response by RFC-0006) as pills adjacent to the doc title. Color
// mapping reuses the PRD-0001 interim design tokens (success / warning / error /
// muted) as a PLACEHOLDER pending design-system decision D9 — the exact status→
// color mapping is revisited once D9 lands. `docOwner` is NOT a badge; it is
// surfaced in the metadata rail by ProjectPage.
//
// Fully null-safe: renders nothing when both fields are absent.

import { Badge, type BadgeVariant } from '@/components/ui/badge';

interface DocBadgesProps {
  docType: string | null;
  docStatus: string | null;
  className?: string;
}

/** Interim placeholder status→token mapping (pending D9). */
function statusVariant(status: string): BadgeVariant {
  const s = status.trim().toLowerCase();
  if (
    ['approved', 'implemented', 'active', 'accepted', 'stable', 'done', 'complete', 'completed'].includes(s)
  ) {
    return 'success';
  }
  if (['deprecated', 'archived', 'rejected', 'superseded', 'abandoned', 'obsolete'].includes(s)) {
    return 'error';
  }
  if (['in-review', 'in review', 'review', 'pending', 'proposed', 'wip', 'in-progress', 'in progress'].includes(s)) {
    return 'warning';
  }
  // draft / unknown → muted placeholder.
  return 'ghost';
}

export function DocBadges({ docType, docStatus, className = '' }: DocBadgesProps) {
  const type = docType?.trim() || null;
  const status = docStatus?.trim() || null;

  if (!type && !status) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`.trim()}>
      {type && (
        <Badge variant="outline" className="uppercase tracking-wide">
          {type}
        </Badge>
      )}
      {status && <Badge variant={statusVariant(status)}>{status}</Badge>}
    </div>
  );
}
